import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, BarChart3, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { getWalletCampaignSpend, getWalletTransactions } from "../apis/wallet";
import { fmtINR } from "../utils/money";
import { fmtIST } from "../utils/datetime";
import {
  CATEGORY_LABELS,
  defaultSpendRange,
  isCredit,
  parseMeta,
  txnSub,
  txnTitle,
} from "../utils/walletTxn";

const PAGE_SIZE = 20;

const WalletCampaignSpend = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const range = defaultSpendRange(90);
  const from = params.get("from") || range.from;
  const to = params.get("to") || range.to;
  const page = Math.max(0, parseInt(params.get("page") || "0", 10) || 0);

  const [data, setData] = useState(null);
  const [txns, setTxns] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [txnLoading, setTxnLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWalletCampaignSpend(campaignId, { from, to });
      if (res.data?.success) setData(res.data.data);
    } catch (_) {
      toast.error("Failed to load campaign spend");
    } finally {
      setLoading(false);
    }
  }, [campaignId, from, to]);

  const loadTxns = useCallback(async () => {
    setTxnLoading(true);
    try {
      const res = await getWalletTransactions({
        campaign_id: campaignId,
        from,
        to,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      if (res.data?.success) setTxns(res.data.data);
    } catch (_) {
    } finally {
      setTxnLoading(false);
    }
  }, [campaignId, from, to, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTxns(); }, [loadTxns]);

  const totalPages = Math.max(1, Math.ceil((txns.total || 0) / PAGE_SIZE));
  const setPage = (p) => {
    const next = new URLSearchParams(params);
    if (p <= 0) next.delete("page");
    else next.set("page", String(p));
    setParams(next, { replace: true });
  };

  if (loading) {
    return (
      <div className="wallet-empty" style={{ minHeight: "40vh" }}>
        <Loader2 className="animate-spin mx-auto" size={22} style={{ color: "var(--blue)" }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="wallet-page">
        <p>Campaign not found in this wallet.</p>
        <Link to="/wallet/spend">Back to spend</Link>
      </div>
    );
  }

  return (
    <div className="wallet-page wspend">
      <button type="button" className="wspend-back" onClick={() => navigate(`/wallet/spend?from=${from}&to=${to}`)}>
        <ArrowLeft size={15} />
        Spend details
      </button>
      <div className="page-head">
        <div>
          <h2>{data.campaign_name}</h2>
          <p>
            {data.status ? `${String(data.status).toUpperCase()} · ` : ""}
            Campaign spend {from} → {to}
          </p>
        </div>
        <div className="grow" />
        <Link
          to={`/campaigns/${campaignId}/analytics`}
          className="ping-btn ping-btn-ghost ping-btn-sm"
        >
          <BarChart3 size={15} />
          Delivery analytics
        </Link>
      </div>

      <div className="wspend-totals">
        <div>
          <span className="k">Gross charged</span>
          <b>{fmtINR(data.gross)}</b>
          <small>{(data.debit_n || (data.billed_n + data.refund_n)).toLocaleString("en-IN")} messages</small>
        </div>
        <div>
          <span className="k">Refunds</span>
          <b className="cr">{fmtINR(data.refunds)}</b>
          <small>{data.refund_n.toLocaleString("en-IN")} failed</small>
        </div>
        <div>
          <span className="k">Net spend</span>
          <b>{fmtINR(data.net)}</b>
          <small>{data.unique_phones.toLocaleString("en-IN")} unique numbers</small>
        </div>
      </div>

      <div className="wspend-split">
        <div className="wallet-card">
          <div className="wallet-card-pad">
            <h3>By category</h3>
            <p className="sub">Net after refunds</p>
          </div>
          {data.categories?.length ? (
            <table className="wallet-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th style={{ textAlign: "right" }}>Billed</th>
                  <th style={{ textAlign: "right" }}>Refunded</th>
                  <th style={{ textAlign: "right" }}>Net</th>
                </tr>
              </thead>
              <tbody>
                {data.categories.map((c) => (
                  <tr key={c.category}>
                    <td>{CATEGORY_LABELS[c.category] || c.category}</td>
                    <td style={{ textAlign: "right" }}>{c.billed_n.toLocaleString("en-IN")}</td>
                    <td style={{ textAlign: "right" }}>{c.refund_n.toLocaleString("en-IN")}</td>
                    <td style={{ textAlign: "right" }}><b>{fmtINR(c.net)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="wallet-empty">No category split</div>
          )}
        </div>
        <div className="wallet-card">
          <div className="wallet-card-pad">
            <h3>By template</h3>
            <p className="sub">Top templates in this campaign</p>
          </div>
          {data.templates?.length ? (
            <table className="wallet-table">
              <thead>
                <tr>
                  <th>Template</th>
                  <th style={{ textAlign: "right" }}>Billed</th>
                  <th style={{ textAlign: "right" }}>Net</th>
                </tr>
              </thead>
              <tbody>
                {data.templates.map((t) => (
                  <tr key={t.template}>
                    <td><span className="wspend-tpl">{t.template}</span></td>
                    <td style={{ textAlign: "right" }}>{t.billed_n.toLocaleString("en-IN")}</td>
                    <td style={{ textAlign: "right" }}><b>{fmtINR(t.net)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="wallet-empty">No templates</div>
          )}
        </div>
      </div>

      <div className="wallet-card wspend-charges" style={{ marginTop: 16 }}>
        <div className="wallet-activity-head">
          <h3 style={{ flex: 1 }}>Charges</h3>
        </div>
        <div className="wspend-charges-body">
          {txnLoading ? (
            <div className="wallet-empty">
              <Loader2 className="animate-spin mx-auto" size={22} style={{ color: "var(--blue)" }} />
            </div>
          ) : txns.rows.length === 0 ? (
            <div className="wallet-empty">No charges in this range</div>
          ) : (
            <table className="wallet-table">
              <thead>
                <tr>
                  <th>Transaction</th>
                  <th>Date</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {txns.rows.map((t) => {
                  const meta = parseMeta(t.meta);
                  const credit = isCredit(t.type, meta, t.sign);
                  return (
                    <tr key={t.id}>
                      <td>
                        <div className="wallet-cell">
                          <span className={`wallet-dir ${credit ? "cr" : "db"}`} aria-hidden><i /></span>
                          <div>
                            <b>{txnTitle(t.type, meta, t.resolved)}</b>
                            <span className="sub">{txnSub(t.type, meta, t.reference_id, t.resolved)}</span>
                          </div>
                        </div>
                      </td>
                      <td style={{ color: "var(--text-2)", whiteSpace: "nowrap" }}>{fmtIST(t.created_at)}</td>
                      <td style={{ textAlign: "right" }}>
                        <span className={credit ? "wallet-amt-cr" : "wallet-amt-db"}>
                          {credit ? "+" : "−"} {fmtINR(t.amount)}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Link to={`/wallet/transactions/${t.id}`} className="ping-btn ping-btn-ghost ping-btn-sm">
                          Details
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {txns.total > 0 && (
          <div className="wallet-pager">
            <span>Page {page + 1} of {totalPages} · {txns.total.toLocaleString("en-IN")}</span>
            <div className="flex gap-2">
              <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)} className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-40">Previous</button>
              <button type="button" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletCampaignSpend;
